import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, FiServer, FiDatabase, FiUsers, FiShoppingBag, 
  FiTruck, FiLock, FiGlobe, FiCode, FiPackage, FiDollarSign,
  FiSmartphone, FiCpu, FiLayers, FiCheckCircle, FiCopy,
  FiChevronDown, FiChevronUp
} from 'react-icons/fi';

const ReportSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-gray-800 rounded-xl overflow-hidden mb-4">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#DC143C]/20 rounded-lg flex items-center justify-center">
            <Icon className="text-[#DC143C]" size={20} />
          </div>
          <h2 className="text-lg font-semibold text-white">{title}</h2>
        </div>
        {isOpen ? <FiChevronUp className="text-gray-400" /> : <FiChevronDown className="text-gray-400" />}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-gray-700">
          {children}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color = "text-white" }) => (
  <div className="bg-gray-700/50 rounded-lg p-3 text-center">
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    <p className="text-xs text-gray-400">{label}</p>
  </div>
);

const TableRow = ({ cells, header = false }) => (
  <tr className={header ? "bg-gray-700" : "hover:bg-gray-700/30"}>
    {cells.map((cell, i) => (
      header ? (
        <th key={i} className="text-left p-3 text-sm font-medium text-gray-300">{cell}</th>
      ) : (
        <td key={i} className="p-3 text-sm text-gray-300">{cell}</td>
      )
    ))}
  </tr>
);

const Badge = ({ children, color = "green" }) => {
  const colors = {
    green: "bg-green-900 text-green-300",
    red: "bg-red-900 text-red-300",
    blue: "bg-blue-900 text-blue-300",
    yellow: "bg-yellow-900 text-yellow-300",
    purple: "bg-purple-900 text-purple-300"
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs ${colors[color]}`}>
      {children}
    </span>
  );
};

const SystemReport = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState('');

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const endpoints = {
    auth: [
      { method: 'POST', path: '/api/auth/register', desc: 'Cadastro de usuário' },
      { method: 'POST', path: '/api/auth/login', desc: 'Login (retorna JWT)' },
      { method: 'POST', path: '/api/auth/forgot-password', desc: 'Solicitar reset de senha' },
      { method: 'POST', path: '/api/auth/reset-password', desc: 'Redefinir senha' },
      { method: 'GET', path: '/api/auth/me', desc: 'Dados do usuário logado' },
    ],
    stores: [
      { method: 'GET', path: '/api/stores', desc: 'Listar lojas' },
      { method: 'POST', path: '/api/stores', desc: 'Criar loja' },
      { method: 'GET', path: '/api/stores/{id}', desc: 'Detalhes da loja' },
      { method: 'PUT', path: '/api/stores/{id}', desc: 'Atualizar loja' },
    ],
    products: [
      { method: 'GET', path: '/api/products', desc: 'Listar produtos' },
      { method: 'POST', path: '/api/products', desc: 'Criar produto' },
      { method: 'GET', path: '/api/products/{id}', desc: 'Detalhes do produto' },
      { method: 'PUT', path: '/api/products/{id}', desc: 'Atualizar produto' },
      { method: 'DELETE', path: '/api/products/{id}', desc: 'Excluir produto' },
    ],
    orders: [
      { method: 'GET', path: '/api/orders', desc: 'Meus pedidos' },
      { method: 'POST', path: '/api/orders', desc: 'Criar pedido' },
      { method: 'GET', path: '/api/orders/{id}', desc: 'Detalhes do pedido' },
      { method: 'GET', path: '/api/orders/{id}/eta', desc: 'Tempo estimado de chegada' },
      { method: 'PUT', path: '/api/orders/{id}/status', desc: 'Atualizar status' },
      { method: 'POST', path: '/api/orders/{id}/accept', desc: 'Aceitar entrega' },
      { method: 'POST', path: '/api/orders/{id}/complete', desc: 'Confirmar entrega' },
    ],
    payments: [
      { method: 'POST', path: '/api/payments/pix/{order_id}', desc: 'Gerar QR Code PIX' },
      { method: 'GET', path: '/api/payments/status/{order_id}', desc: 'Status do pagamento' },
      { method: 'POST', path: '/api/webhooks/mercadopago', desc: 'Webhook Mercado Pago' },
    ],
    admin: [
      { method: 'GET', path: '/api/admin/analytics', desc: 'Métricas e estatísticas' },
      { method: 'GET', path: '/api/admin/stores', desc: 'Listar todas as lojas' },
      { method: 'PUT', path: '/api/admin/stores/{id}/status', desc: 'Ativar/desativar loja' },
      { method: 'GET', path: '/api/admin/pending-drivers', desc: 'Entregadores pendentes' },
      { method: 'POST', path: '/api/admin/verify-driver/{id}', desc: 'Aprovar/rejeitar entregador' },
      { method: 'GET', path: '/api/admin/settings', desc: 'Configurações da plataforma' },
      { method: 'PUT', path: '/api/admin/settings', desc: 'Atualizar configurações' },
      { method: 'GET', path: '/api/admin/export/{type}', desc: 'Exportar dados' },
    ],
    notifications: [
      { method: 'POST', path: '/api/notifications/subscribe', desc: 'Ativar push notifications' },
      { method: 'POST', path: '/api/notifications/unsubscribe', desc: 'Desativar push' },
      { method: 'GET', path: '/api/notifications', desc: 'Listar notificações' },
    ],
  };

  const testCredentials = [
    { perfil: 'Cliente', email: 'cliente@test.com', senha: 'Cliente@123' },
    { perfil: 'Lojista', email: 'loja@test.com', senha: 'Loja@123456' },
    { perfil: 'Entregador', email: 'entrega@test.com', senha: 'Entrega@123' },
    { perfil: 'Admin', email: 'antinybeico10@gmail.com', senha: 'Antonio#6325' },
  ];

  const integrations = [
    { name: 'Mercado Pago', status: 'Ativo', desc: 'Pagamentos PIX', color: 'green' },
    { name: 'Cloudinary', status: 'Ativo', desc: 'Upload de imagens', color: 'green' },
    { name: 'Resend', status: 'Ativo', desc: 'Envio de emails', color: 'green' },
    { name: 'OSRM', status: 'Ativo', desc: 'Cálculo de rotas e ETA', color: 'green' },
    { name: 'Leaflet/OSM', status: 'Ativo', desc: 'Mapas e rastreamento', color: 'green' },
    { name: 'ViaCEP', status: 'Ativo', desc: 'Validação de CEP', color: 'green' },
  ];

  const dbCollections = [
    { name: 'users', desc: 'Usuários (cliente, lojista, entregador, admin)' },
    { name: 'stores', desc: 'Lojas cadastradas' },
    { name: 'products', desc: 'Produtos à venda' },
    { name: 'orders', desc: 'Pedidos realizados' },
    { name: 'notifications', desc: 'Notificações dos usuários' },
    { name: 'push_subscriptions', desc: 'Inscrições push notification' },
    { name: 'password_resets', desc: 'Tokens de reset de senha' },
    { name: 'platform_settings', desc: 'Configurações da plataforma' },
    { name: 'audit_logs', desc: 'Logs de segurança' },
    { name: 'admin_actions', desc: 'Ações administrativas' },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate('/admin')} 
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold">Relatório do Sistema</h1>
            <p className="text-sm text-gray-400">BarueriFlash v1.0</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <StatCard label="Arquivos de Código" value="132" color="text-blue-400" />
          <StatCard label="Linhas Backend" value="3.812" color="text-green-400" />
          <StatCard label="Endpoints API" value="73" color="text-purple-400" />
          <StatCard label="Componentes" value="51" color="text-yellow-400" />
          <StatCard label="Rotas Frontend" value="27" color="text-pink-400" />
          <StatCard label="Coleções DB" value="10" color="text-orange-400" />
        </div>

        {/* Sections */}
        <ReportSection title="Funcionalidades por Perfil" icon={FiUsers} defaultOpen={true}>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {/* Cliente */}
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h3 className="font-semibold text-blue-400 mb-3 flex items-center gap-2">
                <FiUsers /> Cliente
              </h3>
              <ul className="space-y-1 text-sm text-gray-300">
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Cadastro/Login JWT</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Recuperação de senha</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Visualizar produtos com filtros</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Carrinho multi-loja</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Checkout com validação CEP</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Pagamento PIX</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Rastreamento em tempo real</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> ETA (tempo estimado)</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Avaliar pedidos</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Push notifications</li>
              </ul>
            </div>

            {/* Lojista */}
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h3 className="font-semibold text-purple-400 mb-3 flex items-center gap-2">
                <FiShoppingBag /> Lojista
              </h3>
              <ul className="space-y-1 text-sm text-gray-300">
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Criar/gerenciar loja</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> CRUD de produtos</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Upload de fotos (Cloudinary)</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Ver/atualizar pedidos</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Configurar Mercado Pago</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Ver estatísticas</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Ver avaliações</li>
              </ul>
            </div>

            {/* Entregador */}
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h3 className="font-semibold text-orange-400 mb-3 flex items-center gap-2">
                <FiTruck /> Entregador
              </h3>
              <ul className="space-y-1 text-sm text-gray-300">
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Cadastro com documentos</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Upload CNH e placa</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Toggle online/offline</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Ver entregas disponíveis</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Aceitar/completar entrega</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Navegação GPS</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Histórico de ganhos</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Push notifications</li>
              </ul>
            </div>

            {/* Admin */}
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h3 className="font-semibold text-red-400 mb-3 flex items-center gap-2">
                <FiLock /> Administrador
              </h3>
              <ul className="space-y-1 text-sm text-gray-300">
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Dashboard com analytics</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Gráficos de vendas</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Ativar/desativar lojas</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Aprovar entregadores</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Configurar taxas</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Exportar dados (Excel/PDF)</li>
                <li className="flex items-center gap-2"><FiCheckCircle className="text-green-400" size={14} /> Logs de auditoria</li>
              </ul>
            </div>
          </div>
        </ReportSection>

        <ReportSection title="Modelo de Negócio" icon={FiDollarSign}>
          <div className="mt-4 space-y-4">
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h3 className="font-semibold text-green-400 mb-3">Fluxo de Pagamento</h3>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="bg-blue-900 px-3 py-1 rounded">Cliente paga PIX</span>
                <span className="text-gray-500">→</span>
                <span className="bg-purple-900 px-3 py-1 rounded">Vai para Lojista</span>
                <span className="text-gray-500">→</span>
                <span className="bg-red-900 px-3 py-1 rounded">Plataforma cobra comissão</span>
                <span className="text-gray-500">→</span>
                <span className="bg-orange-900 px-3 py-1 rounded">Entregador recebe R$10</span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <TableRow header cells={['Taxa', 'Valor', 'Descrição']} />
                </thead>
                <tbody>
                  <TableRow cells={['Comissão (vendas até R$100)', 'R$ 2,00', 'Por venda']} />
                  <TableRow cells={['Comissão (vendas > R$100)', 'R$ 8,00', 'Por venda']} />
                  <TableRow cells={['Mensalidade Lojista', 'R$ 149,00', 'Isento se comissões atingirem']} />
                  <TableRow cells={['Taxa de Entrega (total)', 'R$ 15,00', 'Paga pelo cliente']} />
                  <TableRow cells={['→ Para Entregador', 'R$ 10,00', '67% do frete']} />
                  <TableRow cells={['→ Para Plataforma', 'R$ 5,00', '33% do frete']} />
                </tbody>
              </table>
            </div>
          </div>
        </ReportSection>

        <ReportSection title="Integrações Externas" icon={FiGlobe}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {integrations.map((int, i) => (
              <div key={i} className="bg-gray-700/30 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="font-medium">{int.name}</p>
                  <p className="text-xs text-gray-400">{int.desc}</p>
                </div>
                <Badge color={int.color}>{int.status}</Badge>
              </div>
            ))}
          </div>
        </ReportSection>

        <ReportSection title="API Endpoints (73 total)" icon={FiCode}>
          <div className="mt-4 space-y-4">
            {Object.entries(endpoints).map(([category, eps]) => (
              <div key={category}>
                <h4 className="text-sm font-semibold text-gray-400 uppercase mb-2">{category}</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      {eps.map((ep, i) => (
                        <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                          <td className="py-2 pr-2 w-20">
                            <span className={`text-xs px-2 py-0.5 rounded font-mono ${
                              ep.method === 'GET' ? 'bg-green-900 text-green-300' :
                              ep.method === 'POST' ? 'bg-blue-900 text-blue-300' :
                              ep.method === 'PUT' ? 'bg-yellow-900 text-yellow-300' :
                              'bg-red-900 text-red-300'
                            }`}>
                              {ep.method}
                            </span>
                          </td>
                          <td className="py-2 font-mono text-xs text-gray-300">{ep.path}</td>
                          <td className="py-2 text-gray-400 text-xs">{ep.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </ReportSection>

        <ReportSection title="Banco de Dados (MongoDB)" icon={FiDatabase}>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <TableRow header cells={['Coleção', 'Descrição']} />
              </thead>
              <tbody>
                {dbCollections.map((col, i) => (
                  <TableRow key={i} cells={[
                    <span className="font-mono text-purple-400">{col.name}</span>,
                    col.desc
                  ]} />
                ))}
              </tbody>
            </table>
          </div>
        </ReportSection>

        <ReportSection title="Segurança" icon={FiLock}>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h4 className="font-semibold mb-3 text-green-400">Autenticação</h4>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>• JWT com expiração de 24 horas</li>
                <li>• bcrypt para hash de senhas</li>
                <li>• Validação de força da senha</li>
                <li>• Mínimo 8 caracteres</li>
                <li>• Maiúscula, minúscula, número, especial</li>
              </ul>
            </div>
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h4 className="font-semibold mb-3 text-blue-400">Proteções</h4>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>• Rate Limiting (200 req/min)</li>
                <li>• Brute Force Protection</li>
                <li>• XSS Prevention</li>
                <li>• Security Headers</li>
                <li>• Input Sanitization</li>
                <li>• Audit Logging</li>
              </ul>
            </div>
          </div>
        </ReportSection>

        <ReportSection title="Credenciais de Teste" icon={FiSmartphone}>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <TableRow header cells={['Perfil', 'Email', 'Senha', '']} />
              </thead>
              <tbody>
                {testCredentials.map((cred, i) => (
                  <tr key={i} className="hover:bg-gray-700/30">
                    <td className="p-3">
                      <Badge color={
                        cred.perfil === 'Admin' ? 'red' :
                        cred.perfil === 'Cliente' ? 'blue' :
                        cred.perfil === 'Lojista' ? 'purple' : 'yellow'
                      }>
                        {cred.perfil}
                      </Badge>
                    </td>
                    <td className="p-3 font-mono text-xs text-gray-300">{cred.email}</td>
                    <td className="p-3 font-mono text-xs text-gray-300">{cred.senha}</td>
                    <td className="p-3">
                      <button 
                        onClick={() => copyToClipboard(`${cred.email} / ${cred.senha}`, cred.perfil)}
                        className="p-1.5 hover:bg-gray-600 rounded transition-colors"
                        title="Copiar"
                      >
                        {copied === cred.perfil ? (
                          <FiCheckCircle className="text-green-400" size={14} />
                        ) : (
                          <FiCopy className="text-gray-400" size={14} />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ReportSection>

        <ReportSection title="Stack Tecnológico" icon={FiCpu}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h4 className="font-semibold mb-2 text-blue-400 flex items-center gap-2">
                <FiLayers size={16} /> Frontend
              </h4>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>• React 19</li>
                <li>• React Router 7</li>
                <li>• TailwindCSS</li>
                <li>• Shadcn/UI</li>
                <li>• Recharts</li>
                <li>• Leaflet</li>
                <li>• Axios</li>
              </ul>
            </div>
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h4 className="font-semibold mb-2 text-green-400 flex items-center gap-2">
                <FiServer size={16} /> Backend
              </h4>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>• FastAPI</li>
                <li>• Python 3.11</li>
                <li>• Pydantic</li>
                <li>• Motor (async MongoDB)</li>
                <li>• WebSockets</li>
                <li>• JWT / bcrypt</li>
              </ul>
            </div>
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h4 className="font-semibold mb-2 text-purple-400 flex items-center gap-2">
                <FiDatabase size={16} /> Infraestrutura
              </h4>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>• MongoDB</li>
                <li>• Cloudinary (imagens)</li>
                <li>• Resend (email)</li>
                <li>• Mercado Pago</li>
                <li>• PWA</li>
                <li>• Emergent Platform</li>
              </ul>
            </div>
          </div>
        </ReportSection>

        <ReportSection title="Estrutura de Arquivos" icon={FiPackage}>
          <div className="mt-4 bg-gray-900 rounded-lg p-4 font-mono text-xs overflow-x-auto">
            <pre className="text-gray-300">{`/app
├── backend/
│   ├── server.py          # API principal (3812 linhas)
│   ├── config.py          # Configurações
│   ├── database.py        # Conexão MongoDB
│   ├── requirements.txt   # Dependências Python
│   ├── .env               # Variáveis de ambiente
│   ├── models/            # Modelos Pydantic
│   ├── services/          # WebSocket, Payment
│   └── utils/             # Security, Helpers
│
├── frontend/
│   ├── public/
│   │   ├── manifest.json
│   │   └── service-worker.js
│   └── src/
│       ├── components/    # Cards, Common, Forms, Maps, UI
│       ├── contexts/      # Auth, Cart
│       ├── hooks/         # useNotifications
│       ├── pages/         # Admin, Auth, Cliente, Entregador, Legal, Lojista
│       ├── services/      # API, WebSocket, Push
│       └── utils/         # Formatters
│
└── memory/
    └── PRD.md`}</pre>
          </div>
        </ReportSection>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-8 pb-8">
          <p>BarueriFlash v1.0 - Marketplace de Eletrônicos</p>
          <p>Relatório gerado em {new Date().toLocaleDateString('pt-BR')}</p>
        </div>
      </main>
    </div>
  );
};

export default SystemReport;
